import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import useFavorites from '../../../hooks/useFavorites';

// Function to generate consistent colors for type_thing values (matches FilterChips)
const getTypeThingColorClass = (typeThing) => {
  if (!typeThing) return 'bg-gray-500';
  
  const colors = [
    'bg-purple-500', 'bg-orange-500', 'bg-red-500', 'bg-pink-500', 
    'bg-indigo-500', 'bg-yellow-500', 'bg-teal-500', 'bg-rose-500',
    'bg-violet-500', 'bg-amber-500', 'bg-cyan-500', 'bg-lime-500'
  ];
  
  let hash = 0;
  for (let i = 0; i < typeThing.length; i++) {
    const char = typeThing.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const ThingCard = ({ product }) => {
  const { toggleFavorite, isProductFavorited, loading } = useFavorites();
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    if (product?.thing_id) {
      setIsLiked(isProductFavorited(product.thing_id, 'thing'));
    }
  }, [product?.thing_id, isProductFavorited]);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 fill-yellow-400/50 text-yellow-400" />
        );
      } else {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 text-gray-300" />
        );
      }
    }
    return stars;
  };

  useEffect(() => {
    if (product?.thing_id) {
      setIsLiked(isProductFavorited(product.thing_id, 'thing'));
    }
  }, [product?.thing_id, isProductFavorited]);

  const handleLike = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!product?.thing_id || loading) return;

    const result = await toggleFavorite(product.thing_id, 'thing');
    if (result.success) {
      setIsLiked(result.isFavorited);
    }
  };

  if (!product) {
    return (
      <div className="animate-pulse bg-muted rounded-lg p-4 h-full flex flex-col space-y-3">
        <div className="bg-muted-foreground/20 rounded aspect-[3/4] w-full" />
        <div className="h-4 bg-muted-foreground/20 rounded w-3/4" />
        <div className="h-3 bg-muted-foreground/10 rounded w-1/2" />
        <div className="h-3 bg-muted-foreground/10 rounded w-2/3" />
      </div>
    );
  }

  return (
    <Link to={`/thing-detail?id=${product.thing_id}`} className="group block">
      <div className="bg-card rounded-lg shadow-subtle hover:shadow-modal transition-gentle hover-lift overflow-hidden h-full flex flex-col sm:min-h-[280px] md:min-h-[320px]">
        <div className="relative aspect-[3/4] overflow-hidden flex-shrink-0 flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div className="relative w-[80px] h-[110px] flex items-center justify-center">
            <Image
              src={product.image_urls?.[0] || '/placeholder.jpg'}
              alt={`${product.title_thing}`}
              className="w-full h-full object-cover rounded"
              low
            />
          </div>
          <button
            onClick={handleLike}
            className={`absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full shadow-modal transition-smooth sm:h-9 sm:w-9 ${
              isLiked
                ? 'bg-accent text-accent-foreground'
                : 'bg-white/90 text-muted-foreground hover:bg-white hover:text-accent'
            }`}
            aria-label="Añadir a favoritos"
          >
            <Icon
              name="Heart"
              className={`h-4 w-4 ${isLiked ? 'fill-current' : ''} sm:h-5 sm:w-5`}
            />
          </button>
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <div className={`${getTypeThingColorClass(product.type_thing)} text-white text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full`}>
              {product.type_thing || 'Sin tipo'}
            </div>
          </div>
        </div>

        <div className="p-2 sm:p-3 flex-1 flex flex-col justify-between">
          <div className="space-y-1">
            <h3 className="font-bold text-sm sm:text-base leading-tight line-clamp-2 text-foreground group-hover:text-primary transition-colors">
              {product.title_thing && product.title_thing.length > 17 ? `${product.title_thing.substring(0, 17)}...` : product.title_thing}
            </h3>
            
            {/* Description for accessories instead of author */}
            {product.description_thing && (
              <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2">
                {product.description_thing.length > 50 
                  ? `${product.description_thing.substring(0, 50)}...` 
                  : product.description_thing}
              </p>
            )}
          </div>

          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-semibold text-primary">
                ${product.price_thing?.toFixed(2) || '0.00'}
              </span>
              {product.available_thing ? (
                <span className="text-[10px] sm:text-xs text-green-600 font-medium">
                  Disponible
                </span>
              ) : (
                <span className="text-[10px] sm:text-xs text-red-500 font-medium">
                  No disponible
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ThingCard;

